import React, { useMemo, useState } from 'react';
import './flightOutLineSection.less';

import { FaPlane } from 'react-icons/fa';
import { AiOutlineDown, AiOutlineSwap } from 'react-icons/ai';
import { Button, Skeleton, Tooltip } from 'antd';
import moment from 'moment';
import FlightBaggage from '../FlightBaggage/FlightBaggage';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import FlightVerticalOutLineSection from './FlightVerticalOutLineSection';
import { Product } from '../../../../ShoppingCart/interfaces';
import ShoppingCartAdditionalItem from '../../../../ShoppingCart/products/ShoppingCartAdditionalItem/ShoppingCartAdditionalItem';
import useGetFlightAdditionalFares from '../../../../ViewFlight/ViewFlightApi';
import AdditionalFaresFlightModal from '../AdditionalFaresFlightModal';
import { isEmpty } from 'lodash';
import AmenitiesTooltip from '../../../../ViewFlight/AdditionalFaresFlightBox/AmenitiesTooltip';
import {
  CabinOption,
  CABIN_LABEL_KEYS,
} from '@skygroup/shared/utils/cabinClass';
import {
  formatMinutes,
  getDayOffset,
  getLegDurationMinutes,
} from '@skygroup/shared/utils/flightDuration';

interface Props {
  flightData: any;
  flightOutlineData: any;
  flightSubProducts?: Product[];
  isMinimized?: boolean;
  flightIdentifier: string;
  tsBookingData?: any;
  setDynamicFlights?: any;
  isFlightFrom?: boolean;
  isDynamicFlight?: boolean;
  showProductPrice?: boolean;
  /**
   * Dedicated content-suppression switch for the cabin pill — deliberately NOT
   * `isCompact`. `isCompact` is a styling flag reused across several unrelated
   * surfaces (`ReselectFlightListComponent`, `SearchResultsBundleFlight` — the
   * Dynamic Package "Change Flight" modal) purely for button/price sizing.
   * Gating on it once already leaked the pill's absence into that modal. Only
   * the bot chat widget (`SearchResultList.tsx`) sets this prop.
   */
  hideCabinTag?: boolean;
  /**
   * Phone layout whatever the viewport width — for a phone-sized container on
   * a desktop page (the bot chat's card row). The row is a single DOM for every
   * width now; this only adds the `fl-leg--force-compact` class so the stacked
   * styles apply even where container queries are unavailable.
   */
  forceMobileLayout?: boolean;
  /**
   * Render the operating carrier (small logo, airline name, flight number) at
   * the start of the row. Cards that pass it drop their own big airline
   * column — one compact logo per leg instead of a 120px column per card.
   */
  showCarrier?: boolean;
}

/**
 * One flight leg (outbound, return, or one multi-city leg) as a single compact
 * row:
 *
 *   06:00 ───── 16h ─────●───── 14:00⁺¹     LY 001 + LY 005
 *   TLV        1 stop · IST ▾       JFK      Economy  🧳✓ 👜✓
 *   Wed, 01 Jul                 Thu, 02 Jul
 *
 * WHY ONE DOM FOR EVERY WIDTH
 * ---------------------------
 * The row used to switch DOM on `isMobileWidth`, so the cart, the change-flight
 * modal and the bot widget — narrow containers on a wide viewport — got the
 * desktop layout squeezed into them. The row is now laid out by CSS container
 * queries against its own width (flightOutLineSection.less), so every surface
 * gets the layout that fits the space it actually has.
 *
 * DURATION
 * --------
 * From `getLegDurationMinutes` — the supplier's own leg total, or flying time +
 * ground time — never `last.arrival - first.departure`, which subtracts two
 * different airports' wall clocks (see shared/utils/flightDuration.ts). Unknown
 * durations render nothing rather than a wrong number.
 */
const FlightOutLineSection: React.FC<Props> = ({
  flightData,
  flightOutlineData,
  flightSubProducts,
  isMinimized,
  tsBookingData,
  flightIdentifier,
  setDynamicFlights,
  isFlightFrom,
  isDynamicFlight = false,
  showProductPrice = true,
  hideCabinTag = false,
  forceMobileLayout = false,
  showCarrier = false,
}) => {
  const {
    searchDatePicker: t,
    checkout: checkout_t,
    viewFlight: viewFlight_t,
    general: general_t,
  } = useSelector((data: RootState) => data.config.translations);
  const { siteConfig } = useSelector((state: RootState) => state.config.config);
  const [showStopDetails, setShowStopDetails] = useState(false);

  const segments: any[] = useMemo(
    () =>
      Array.isArray(flightData) ? flightData : flightData ? [flightData] : [],
    [flightData]
  );

  const carrier = segments[0]?.airline;
  const carrierLogo: string | undefined = carrier?.image || carrier?.logo;
  const fareData = segments[0]?.fare;
  const fareName = fareData?.name;
  const fareIdentifier = flightData?.identifier;

  // Wall-clock at each airport, encoded as UTC — always format with .utc().
  const departMoment = moment(flightOutlineData.departure_date * 1000).utc();
  const arrivalMoment = moment(flightOutlineData.arrival_date * 1000).utc();
  const dayOffset = getDayOffset(
    flightOutlineData.departure_date,
    flightOutlineData.arrival_date
  );

  const durationLabel = formatMinutes(
    getLegDurationMinutes(segments),
    general_t?.h,
    general_t?.min
  );

  // Connection airports only — every segment's `to` except the last one.
  const stopCodes: string[] = segments
    .slice(0, -1)
    .map((segment) => segment?.to?.code)
    .filter(Boolean);
  const numOfStops: number = flightOutlineData.num_of_stops ?? 0;
  const hasStops = numOfStops > 0;
  const stopsLabel =
    numOfStops === 0
      ? t.directFlight
      : numOfStops === 1
      ? t.stop_1
      : `${numOfStops} ${t.stops}`;

  const memoizedBookingData = useMemo(() => tsBookingData, []);
  // Only the dynamic (per-direction) results fetch extra fares — a network call.
  const { data: additionalFares, isFetching: isAdditionalFaresFetching } =
    useGetFlightAdditionalFares(
      isDynamicFlight ? memoizedBookingData : null,
      isDynamicFlight ? flightIdentifier : ''
    );
  const [isFareModalOpen, setIsFareModalOpen] = useState(false);

  const fullDateTooltip = (m: moment.Moment) =>
    `${t.date ?? ''} ${m.format(
      `${siteConfig?.dateFormat || 'DD/MM/YYYY'} HH:mm`
    )}`.trim();

  const renderPoint = (
    kind: 'dep' | 'arr',
    m: moment.Moment,
    code?: string,
    name?: string
  ) => (
    <div className={`fl-leg-point fl-leg-point--${kind}`}>
      <div className="fl-leg-time">
        {m.format('HH:mm')}
        {kind === 'arr' && dayOffset !== 0 ? (
          <Tooltip trigger={['hover', 'click']} title={fullDateTooltip(m)}>
            <sup className="fl-leg-day-offset">
              {dayOffset > 0 ? `+${dayOffset}` : dayOffset}
            </sup>
          </Tooltip>
        ) : null}
      </div>
      <Tooltip
        trigger={['hover', 'click']}
        title={name && name !== code ? name : undefined}
      >
        <div className="fl-leg-code">{code}</div>
      </Tooltip>
      <Tooltip trigger={['hover', 'click']} title={fullDateTooltip(m)}>
        <div className="fl-leg-date">{m.format('ddd, DD MMM')}</div>
      </Tooltip>
    </div>
  );

  const stopsContent = (
    <>
      <span className="fl-leg-stops-count">{stopsLabel}</span>
      {stopCodes.length > 0 && (
        <span className="fl-leg-stops-codes">{stopCodes.join(', ')}</span>
      )}
      {hasStops && (
        <AiOutlineDown
          className={`fl-leg-stops-chevron${showStopDetails ? ' is-open' : ''}`}
        />
      )}
    </>
  );

  return (
    <>
      <div
        className={[
          'flight-outline-section',
          'fl-leg',
          hasStops ? 'fl-leg--stops' : 'fl-leg--direct',
          isMinimized ? 'fl-leg--minimized' : '',
          showCarrier ? 'fl-leg--carrier' : '',
          forceMobileLayout ? 'fl-leg--force-compact' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="flight-leg"
      >
        {/* The section is the CSS container; this inner grid is what the
            container queries re-flow (a container can't restyle itself). */}
        <div className="fl-leg-inner">
          {showCarrier && (
            <Tooltip
              trigger={['hover', 'click']}
              title={
                carrier?.name
                  ? `${carrier.name}${
                      carrier?.id
                        ? ` - ${t.airlineCode ?? ''} ${carrier.id}`
                        : ''
                    }`
                  : undefined
              }
            >
              <div className="fl-leg-carrier">
                {carrierLogo ? (
                  <img
                    className="fl-leg-carrier-logo"
                    src={carrierLogo}
                    alt={carrier?.name ?? ''}
                    loading="lazy"
                  />
                ) : (
                  <span className="fl-leg-carrier-code">{carrier?.id}</span>
                )}
                <div className="fl-leg-carrier-text">
                  <span className="fl-leg-carrier-name">
                    {flightOutlineData.stop_airline_names || carrier?.name}
                  </span>
                </div>
              </div>
            </Tooltip>
          )}
          <div className="fl-leg-route">
            {renderPoint(
              'dep',
              departMoment,
              flightOutlineData.from?.code,
              flightOutlineData.from?.name
            )}

            <div className="fl-leg-track">
              {/* the flight numbers over the line (every segment's when it stops) */}
              <div className="fl-leg-flight-nos">
                {flightOutlineData.flight_joint_name}
              </div>
              <div className="fl-leg-line" aria-hidden="true">
                <span className="fl-leg-line-bar" />
                {stopCodes.map((code, index) => (
                  <span
                    key={`${code}-${index}`}
                    className="fl-leg-stop-dot"
                    style={{
                      insetInlineStart: `${
                        ((index + 1) / (stopCodes.length + 1)) * 100
                      }%`,
                    }}
                  />
                ))}
                <FaPlane className="fl-leg-plane" />
              </div>
              {/* under the line: the total time, then direct / the stops */}
              <div className="fl-leg-track-foot">
                {durationLabel ? (
                  <span className="fl-leg-duration">{durationLabel}</span>
                ) : null}
                {hasStops ? (
                  <button
                    type="button"
                    className="fl-leg-stops"
                    aria-expanded={showStopDetails}
                    onClick={() => setShowStopDetails((prev) => !prev)}
                  >
                    {stopsContent}
                  </button>
                ) : (
                  <div className="fl-leg-stops is-direct">{stopsContent}</div>
                )}
              </div>
            </div>

            {renderPoint(
              'arr',
              arrivalMoment,
              flightOutlineData.to?.code,
              flightOutlineData.to?.name
            )}
          </div>

          <div className="fl-leg-meta">
            <div className="fl-leg-meta-line">
              {/*
              Cabin actually delivered on this leg. Conditionally rendered — not
              merely hidden — so an offer with no cabin (every ALP/SKY360 result
              today) adds zero DOM and shifts no layout. Suppressed only via the
              dedicated `hideCabinTag` prop (bot chat widget), never `isCompact`.
            */}
              {flightOutlineData.cabin && !hideCabinTag && (
                <span className="flight-cabin-tag">
                  <span className="flight-cabin-pill">
                    {
                      t[
                        CABIN_LABEL_KEYS[flightOutlineData.cabin as CabinOption]
                      ]
                    }
                    {flightOutlineData.cabinMixed && (
                      <span className="flight-cabin-mixed-hint">
                        {' '}
                        · {t.mixedCabin}
                      </span>
                    )}
                  </span>
                </span>
              )}
            </div>

            {(fareName || flightOutlineData.operatedBy) && (
              <div className="fl-leg-meta-line fl-leg-meta-sub">
                {fareName ? (
                  <span className="fl-leg-fare">
                    {`${viewFlight_t?.ticketFare ?? ''} ${fareName}`.trim()}
                    {showCarrier && (
                      // The fare's amenities used to hang off the big airline
                      // column; with that column gone they sit by the fare name.
                      <span className="fl-leg-amenities">
                        <AmenitiesTooltip
                          flightFrom={segments[0]}
                          isShowFullText={false}
                          showFareName={false}
                        />
                      </span>
                    )}
                  </span>
                ) : null}
                {flightOutlineData.operatedBy ? (
                  <Tooltip
                    trigger={['hover', 'click']}
                    placement="bottom"
                    title={
                      flightOutlineData?.operatedByAirlineLogos?.length > 0 ? (
                        <div className="logos-wrapper">
                          {flightOutlineData.operatedByAirlineLogos.map(
                            (logo: string) => (
                              <img
                                key={logo}
                                src={logo}
                                className="logo small"
                                alt=""
                              />
                            )
                          )}
                        </div>
                      ) : undefined
                    }
                  >
                    <span className="fl-leg-operated">
                      {`${t.operated_by_airlines}: ${flightOutlineData.operatedBy}`}
                    </span>
                  </Tooltip>
                ) : null}
              </div>
            )}

            <div className="fl-leg-meta-line fl-leg-meta-extras">
              <FlightBaggage
                variant="compact"
                insIncludeSuitCase={
                  !!flightOutlineData.baggage?.is_include_suitcase
                }
                isIncludeTrolly={!!flightOutlineData.baggage?.is_include_trolly}
              />
              {flightOutlineData.available_seats > 0 ? (
                <span
                  className={`available-seats available-seats-${flightOutlineData.available_seats}`}
                >
                  {flightOutlineData.available_seats} {general_t.availableSeats}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <ShoppingCartAdditionalItem
        subProducts={flightSubProducts}
        text={checkout_t.additionalBaggage}
        isBaggages
        showProductPrice={showProductPrice}
      />

      {showStopDetails && (
        <FlightVerticalOutLineSection flightData={segments} />
      )}

      {isDynamicFlight && (
        <div className="open-fares-slot">
          {isAdditionalFaresFetching ? (
            <Skeleton.Button active size="small" />
          ) : !isEmpty(additionalFares) ? (
            // a quiet pill like the leg's "change flight" - not a second primary action next to "Add to cart"
            <Button
              className="open-fares-modal-button"
              icon={<AiOutlineSwap />}
              onClick={() => setIsFareModalOpen(true)}
            >{`${
              fareName
                ? `${viewFlight_t.selectedFare} ${fareName}`
                : viewFlight_t.openFares
            }`}</Button>
          ) : null}
        </div>
      )}

      <AdditionalFaresFlightModal
        additionalFares={additionalFares}
        fareName={fareName}
        isFareModalOpen={isFareModalOpen}
        isFlightFrom={isFlightFrom}
        setDynamicFlights={setDynamicFlights}
        setIsFareModalOpen={setIsFareModalOpen}
        fareIdentifier={fareIdentifier}
        currentSegments={segments}
      />
    </>
  );
};

export default FlightOutLineSection;
